import React, { useState, useCallback, useEffect } from 'react';
import classnames from 'classnames';
import { CarouselProvider, Slider, Slide, DotGroup } from 'pure-react-carousel';
import 'pure-react-carousel/dist/react-carousel.es.css';

// Types
import { NewFeatureCarouselModalType, SlideType } from './NewFeatureCarouselModal.types';

// Components
import { WpModal, WpModalBody, WpModalFooter } from 'Wpds/components/modal';
import { WpButton, WpButtonGroup } from 'Wpds/components/button';
import WpIcon from 'Wpds/components/icon/WpIcon';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

// Utils
import { trackEvent } from 'Shared/tracking/tracking';

// Constants
import TrackingConstants from 'Shared/constants/Tracking.constants';

// Styles
import './NewFeatureCarouselModal.scss';

type NewFeatureCarouselModalProps = {
  isOpen: boolean;
  toggleIsOpen: () => void;
  data: NewFeatureCarouselModalType;
  additionalButton?: React.ReactNode;
  zIndex?: number;
};

const NewFeatureCarouselModal = ({
  isOpen,
  toggleIsOpen,
  additionalButton,
  data,
  zIndex,
}: NewFeatureCarouselModalProps) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [activeSlide, setActiveSlide] = useState(data.slides[0]);
  const { tr } = useTranslation();

  const numberOfSlides = data.slides.length;
  const isFirstSlide = currentSlide === 0;
  const isLastSlide = currentSlide === numberOfSlides - 1;

  const showDots = numberOfSlides > 1;
  const showBackButton = numberOfSlides > 1;

  const baseClassName = 'NewFeatureCarouselModal';

  useEffect(() => {
    if (data.slug) {
      trackEvent('Viewed New Feature Carousel Modal', { [TrackingConstants.NEW_FEATURE_SLUG]: data.slug });
    }
  }, []);

  useEffect(() => {
    setActiveSlide(data.slides[currentSlide]);
  }, [currentSlide]);

  const handleMoveToPreviouSlide = useCallback(() => {
    setCurrentSlide(currentSlide - 1);
  }, [currentSlide]);

  const handleMoveToNextSlide = useCallback(() => {
    if (isLastSlide) {
      toggleIsOpen();
      if (data.slug) {
        trackEvent('Completed New Feature Carousel Modal', { [TrackingConstants.NEW_FEATURE_SLUG]: data.slug });
      }
    } else {
      setCurrentSlide(currentSlide + 1);
    }
  }, [currentSlide, isLastSlide]);

  const handleDismissModal = useCallback(() => {
    if (data.slug) {
      trackEvent(
        'Dismissed New Feature Carousel Modal',
        {
          [TrackingConstants.NEW_FEATURE_SLUG]: data.slug,
          last_viewed_slide: activeSlide.slug,
        },
        { integrations: { Calixa: false } }
      );
    }
    toggleIsOpen();
  }, [activeSlide]);

  const SlideMarkup = ({ slide, index }: { slide: SlideType; index: number }) => {
    return (
      <Slide index={index} classNameVisible={`${baseClassName}__carousel__slide`}>
        <div className={`${baseClassName}__slide`}>
          <div className={`${baseClassName}__slide__img-wrapper`} style={{ backgroundColor: slide.imageBgColor }}>
            {slide.image && (
              <img
                width={slide.imageWidth}
                src={slide.image}
                alt={slide.title}
                className={`${baseClassName}__slide__img-wrapper__img`}
                style={{ width: slide.imageWidth }}
              />
            )}
          </div>
          <div className={`${baseClassName}__slide__details`}>
            <h4>{slide.title}</h4>
            <p className={`${baseClassName}__slide__description`}>{slide.description}</p>
            {slide.footer && <div className={`${baseClassName}__slide__footer`}>{slide.footer}</div>}
          </div>
        </div>
      </Slide>
    );
  };

  const Dots = data => {
    const activeSlideIndex = data.currentSlide;
    const slidesCount = data.totalSlides;

    const dots = [];
    for (let i = 0; i < slidesCount; i += 1) {
      const dotClasses = classnames(
        `${baseClassName}__dots__dot`,
        i === activeSlideIndex && `${baseClassName}__dots__dot--active`
      );
      dots.push(<div className={dotClasses} key={`${baseClassName}-dots-${i}`} />);
    }

    return <div className={`${baseClassName}__dots`}>{dots}</div>;
  };

  if (!isOpen) return null;

  return (
    <WpModal
      isOpen={isOpen}
      size="sm"
      dismissOnBackdrop={false}
      innerClassName={`${baseClassName}__modal-inner`}
      zIndex={zIndex}
    >
      <WpModalBody padding={0}>
        <div className={`${baseClassName}__close__wrapper`}>
          <div className={`${baseClassName}__close__wrapper__btn`}>
            <WpButton variant="tertiary" onClick={handleDismissModal} qaTest="dismiss-new-feature-modal-button">
              <WpIcon name="times" />
            </WpButton>
          </div>
        </div>
        <CarouselProvider
          currentSlide={currentSlide}
          naturalSlideWidth={100}
          naturalSlideHeight={125}
          totalSlides={numberOfSlides}
          dragEnabled={false}
          touchEnabled={false}
          disableKeyboard={true}
          isIntrinsicHeight
          className={`${baseClassName}__carousel`}
        >
          <Slider className={`${baseClassName}__carousel__slider`}>
            {data.slides.map((slide, index) => (
              <SlideMarkup slide={slide} index={index} key={`${slide.title}-${index}`} />
            ))}
          </Slider>
          {showDots && <DotGroup renderDots={Dots} />}
        </CarouselProvider>
      </WpModalBody>
      <WpModalFooter>
        <WpButtonGroup>
          {additionalButton && <>{additionalButton}</>}
          {showBackButton && (
            <WpButton variant="tertiary" onClick={handleMoveToPreviouSlide} disabled={isFirstSlide}>
              {tr('perform.new_feature_carousel.back_button')}
            </WpButton>
          )}
          <WpButton
            onClick={handleMoveToNextSlide}
            variant={activeSlide.nextBtnVariant}
            qaTest={activeSlide.nextBtnTestId && activeSlide.nextBtnTestId}
          >
            {activeSlide.nextBtnLabel ? activeSlide.nextBtnLabel : tr('perform.new_feature_carousel.next_button')}
          </WpButton>
        </WpButtonGroup>
      </WpModalFooter>
    </WpModal>
  );
};

export default NewFeatureCarouselModal;
